import React, { useState, useEffect } from 'react';
import Modal from './Modal';

interface ImagePreviewProps {
  images: Array<{ url: string; caption?: string }>;
  initialImageIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ images, initialImageIndex, isOpen, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialImageIndex);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Image ${currentIndex + 1} of ${images.length}`}
      size="xl"
      actions={{
        secondary: {
          label: 'Close',
          onClick: onClose
        }
      }}
    >
      <div className="relative flex items-center justify-center">
        {/* Previous button */}
        {images.length > 1 && (
          <button
            onClick={handlePrevious}
            className="absolute left-4 p-2 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-75 focus:outline-none"
            aria-label="Previous image"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Image */}
        <img
          src={images[currentIndex].url}
          alt={images[currentIndex].caption || `Image ${currentIndex + 1}`}
          className="max-h-[80vh] w-auto object-contain rounded-lg"
        />

        {/* Next button */}
        {images.length > 1 && (
          <button
            onClick={handleNext}
            className="absolute right-4 p-2 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-75 focus:outline-none"
            aria-label="Next image"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Image caption */}
        {images[currentIndex].caption && (
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <span className="px-4 py-2 rounded-md bg-black bg-opacity-50 text-white text-sm">
              {images[currentIndex].caption}
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ImagePreview; 